import LikertQuestion from '@/components/LikertQuestion';
import { DOMAIN_QUESTIONS, EXTENDED_QUESTIONS, DOMAIN_NAMES } from '@/lib/assessmentQuestions';
import { QUESTION_KEY_PREVIEW_LENGTH } from '@/lib/constants';
import {
  GitBranch,
  CheckSquare,
  Repeat,
  ShieldCheck,
  Database,
  CirclesFour,
  Users,
  Scales,
} from '@phosphor-icons/react';

// Module-level constants — stable references, never recreated across renders.
const AWSAI_PART1_DOMAINS = ['domain1', 'domain2'];
const AWSAI_PART2_DOMAINS = ['domain3', 'domain4'];
const EXTENDED_DOMAINS = ['e1', 'e2', 'e3', 'e4'];

const DOMAIN_META = {
  domain1: { icon: GitBranch, blurb: 'How much your workflows depend on specific individuals to make day-to-day decisions.' },
  domain2: { icon: CheckSquare, blurb: 'How consistently tasks are performed across team members and over time.' },
  domain3: { icon: Repeat, blurb: 'How much repetitive manual work the team performs that could be automated.' },
  domain4: { icon: ShieldCheck, blurb: 'How effectively errors are prevented and caught before they reach downstream.' },
  e1: { icon: Database, blurb: 'Quality, structure, and accessibility of the data behind your workflows.' },
  e2: { icon: CirclesFour, blurb: 'Maturity and integration of the systems and tools the team uses.' },
  e3: { icon: Users, blurb: 'Team capability, digital literacy, and AI-readiness mindset.' },
  e4: { icon: Scales, blurb: 'Governance, policies, and accountability for AI and automation.' },
};

const SectionHeader = ({ domain }) => {
  const meta = DOMAIN_META[domain];
  const Icon = meta.icon;
  return (
    <div className="flex items-start gap-3 mb-5 pb-4 border-b border-slate-100">
      <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center shadow-sm">
        <Icon size={22} weight="bold" className="text-white" />
      </div>
      <div>
        <h3 className="text-lg sm:text-xl font-semibold text-slate-900 leading-tight">
          {DOMAIN_NAMES[domain]}
        </h3>
        <p className="text-sm text-slate-500 mt-1 max-w-xl leading-snug">{meta.blurb}</p>
      </div>
    </div>
  );
};

/**
 * Renders a list of Likert questions for a given set of domains.
 * Used for both AWSAI core domains and the Extended module.
 */
const QuestionRenderer = ({ domains, questionsMap, valuesMap, onUpdate }) => (
  <>
    {domains.map(domain => (
      <section key={domain} className="mb-8">
        <SectionHeader domain={domain} />
        <div className="space-y-3">
          {questionsMap[domain].map((question, idx) => (
            <LikertQuestion
              key={`${domain}-${question.substring(0, QUESTION_KEY_PREVIEW_LENGTH)}`}
              question={question}
              value={valuesMap[domain][idx]}
              onChange={(value) => onUpdate(domain, idx, value)}
              testIdPrefix={`${domain}-q${idx + 1}`}
              domain={domain}
            />
          ))}
        </div>
      </section>
    ))}
  </>
);

// Convenience presets — share QuestionRenderer with stable domain arrays.
export const AwsaiPart1 = ({ values, onUpdate }) => (
  <QuestionRenderer
    domains={AWSAI_PART1_DOMAINS}
    questionsMap={DOMAIN_QUESTIONS}
    valuesMap={values}
    onUpdate={onUpdate}
  />
);

export const AwsaiPart2 = ({ values, onUpdate }) => (
  <QuestionRenderer
    domains={AWSAI_PART2_DOMAINS}
    questionsMap={DOMAIN_QUESTIONS}
    valuesMap={values}
    onUpdate={onUpdate}
  />
);

export const ExtendedModule = ({ values, onUpdate }) => (
  <QuestionRenderer
    domains={EXTENDED_DOMAINS}
    questionsMap={EXTENDED_QUESTIONS}
    valuesMap={values}
    onUpdate={onUpdate}
  />
);

export default QuestionRenderer;
